/**
 * Cloudinary Image Optimization Utilities
 * 
 * These functions help optimize Cloudinary images by adding transformation parameters
 * to prevent pixelation when images are displayed at smaller sizes.
 */

/**
 * Optimizes a Cloudinary URL with transformation parameters
 * @param {string} imageUrl - The original Cloudinary URL
 * @param {number} width - Desired width in pixels
 * @param {number} height - Desired height in pixels
 * @param {string} quality - Image quality ('auto', 'good', 'eco', 'low', etc.)
 * @returns {string} - Optimized Cloudinary URL
 */
export const getOptimizedImageUrl = (imageUrl, width = 800, height = 600, quality = 'auto') => {
  if (!imageUrl || !imageUrl.includes('cloudinary.com')) {
    return imageUrl;
  }
  
  // Parse the Cloudinary URL to add transformation parameters
  const urlParts = imageUrl.split('/upload/');
  if (urlParts.length !== 2) {
    return imageUrl;
  }
  
  const baseUrl = urlParts[0] + '/upload';
  const transformations = `w_${width},h_${height},c_fill,q_${quality},f_auto`;
  const imagePath = urlParts[1];
  
  return `${baseUrl}/${transformations}/${imagePath}`;
};

/**
 * Predefined image sizes for different contexts
 */
const IMAGE_SIZES = {
  thumbnail: { width: 200, height: 150 },
  small: { width: 400, height: 300 },
  medium: { width: 800, height: 600 },
  large: { width: 1200, height: 800 },
  xlarge: { width: 1600, height: 1200 }
};

/**
 * Gets an optimized image URL for a specific size context
 * @param {string} imageUrl - The original Cloudinary URL
 * @param {string} size - Size context ('thumbnail', 'small', 'medium', 'large', 'xlarge')
 * @returns {string} - Optimized Cloudinary URL
 */
export const getImageUrl = (imageUrl, size = 'medium') => {
  const sizes = IMAGE_SIZES;
  const { width, height } = sizes[size] || sizes.medium;
  return getOptimizedImageUrl(imageUrl, width, height);
};

/**
 * Gets an optimized image URL with custom dimensions
 * @param {string} imageUrl - The original Cloudinary URL
 * @param {number} width - Custom width
 * @param {number} height - Custom height
 * @param {string} quality - Image quality
 * @returns {string} - Optimized Cloudinary URL
 */
export const getCustomImageUrl = (imageUrl, width, height, quality = 'auto') => {
  return getOptimizedImageUrl(imageUrl, width, height, quality);
};

/**
 * Gets a responsive image URL that adapts to different screen sizes
 * @param {string} imageUrl - The original Cloudinary URL
 * @param {Object} breakpoints - Object with breakpoint sizes
 * @returns {Object} - Object with responsive image URLs
 */
export const getResponsiveImageUrl = (imageUrl, breakpoints = {
  mobile: { width: 400, height: 300 },
  tablet: { width: 800, height: 600 },
  desktop: { width: 1200, height: 800 }
}) => {
  const responsive = {};
  
  Object.entries(breakpoints).forEach(([key, { width, height }]) => {
    responsive[key] = getOptimizedImageUrl(imageUrl, width, height);
  });
  
  return responsive;
};

